import React, { memo, useState } from 'react';
import { InlineTooltip, InlineTooltipPosition } from '@/modules/Tooltip';
import { ListWithDataInHoverItem as ListItem, ListWithDataInHoverColor } from '../../types';
import { getColorValue } from '../../utils/colors';

export interface ListWithDataInHoverItemProps {
  /** Datos del elemento */
  item: ListItem;
  /** Índice del elemento */
  index: number;
  /** Color semántico del indicador */
  color: ListWithDataInHoverColor;
  /** Tamaño del indicador */
  indicatorSize: number;
  /** Gap entre elementos */
  gap: number;
  /** Si mostrar tooltip */
  showTooltip: boolean;
  /** Si mostrar el porcentaje junto al nombre */
  showPercent: boolean;
  /** Posición del tooltip */
  tooltipPosition: InlineTooltipPosition;
  /** Callback cuando se hace hover */
  onHover?: (item: ListItem, index: number) => void;
  /** Callback cuando se sale del hover */
  onLeave?: (item: ListItem, index: number) => void;
}

/**
 * Elemento individual de la lista con datos en hover
 * Molécula que combina indicador de color, texto y tooltip
 */
export const ListWithDataInHoverItem = memo(
  ({
    item,
    index,
    color,
    indicatorSize,
    gap,
    showTooltip,
    showPercent,
    tooltipPosition,
    onHover,
    onLeave,
  }: ListWithDataInHoverItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    // Normalizar valores
    const normalizedName = item.name !== null ? String(item.name) : '';
    const normalizedPercent = Math.max(0, Math.min(100, Number(item.percent) || 0));
    const colorValue = getColorValue(color);

    // Handlers de eventos
    const handleMouseEnter = () => {
      setIsHovered(true);
      onHover?.(item, index);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      onLeave?.(item, index);
    };

    return (
      <div
        className="ui-relative ui-flex ui-cursor-default ui-items-center"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ gap: `${gap}px` }}
      >
        {/* Indicador de color */}
        <span
          className="ui-block ui-flex-shrink-0 ui-rounded-full"
          style={{
            width: `${indicatorSize}px`,
            height: `${indicatorSize}px`,
            backgroundColor: colorValue,
          }}
        />
        {/* Nombre */}
        <span className="ui-text-body-sm-regular ui-text-primary">{normalizedName}</span>
        {/* Porcentaje (más grande y bold) */}
        {showPercent && (
          <span className="ui-text-body-md-bold ui-font-body-md-bold ui-text-primary">
            {normalizedPercent}%
          </span>
        )}
        {/* Tooltip con porcentaje */}
        {showTooltip && (
          <InlineTooltip
            label={`${normalizedPercent}%`}
            isVisible={isHovered}
            position={tooltipPosition}
          />
        )}
      </div>
    );
  },
);

ListWithDataInHoverItem.displayName = 'ListWithDataInHoverItem';
